/**
 * Review — wireframe 2 (W1-18): the queue, the row detail, suggestion
 * acceptance, verdict chips.
 *
 * The screen renders verdicts; it decides nothing (ADR-0014). A chip's text
 * is the service's failing-check name; an Accept button posts a recorded
 * human acceptance (D-16); a correction posts the new value and lets the
 * service compute what that releases.
 */

import { Alert, Button, Card, buttonClass } from '@fiveox/ui'
import {
  BadgeCheck,
  Check,
  ChevronDown,
  ChevronRight,
  Columns3,
  Eye,
  EyeOff,
  FileDown,
  Lightbulb,
  ListChecks,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { CLIENT, DOCUMENT, useEvaluation } from '../lib/access'
import {
  ApiError,
  type DocumentRecord,
  type ReviewBody,
  type ReviewReconRow,
  type ReviewRow,
  type ReviewSuggestion,
  documents,
  exportFacts,
  getOrgId,
  review,
} from '../lib/api'
import { clientPath } from '../lib/clientScope'
import {
  conversionWords,
  correctionFieldFor,
  excludeChoices,
  offersConversion,
  offersCorrection,
  rateChoices,
  rateLabel,
  rateTypedBy,
} from '../lib/correction'
import { documentName } from '../lib/documentName'
import { queueGroups } from '../lib/workbooks'
import { choiceCorrection, choiceLabel, flagChoices } from '../lib/flagChoices'
import { rawDataQueue, rawDataWaitingWords } from '../lib/invoicePreview'
import { foreignValueWords, invoiceTypeWords } from '../lib/invoiceType'
import { belowTotalNote } from '../lib/registerTotals'
import { SPLIT_FIELD, splitGroupWords, splitWords, suggestionGroupKey } from '../lib/splitBill'
import { taxRoundingWords } from '../lib/taxRounding'
import { groupByParty, groupHeading, groupingWords, partyOf } from '../lib/supplierGroups'
import { type Member, fetchMembers } from '../lib/people'
import { session } from '../lib/session'
import { whenWords } from '../lib/when'
import { useClient } from './ClientLayout'
import { ColumnMapper } from './ColumnMapper'
import { HsnQueueCard } from './HsnQueueCard'
import { STAGE_LABELS } from './Intake'
import { TrialBalancePane } from './TrialBalancePane'

const CHIP_LABELS: Record<string, string> = {
  gstin_checksum: 'GSTIN fails checksum',
  taxable_missing: 'No taxable value',
  tax_math: 'Tax ≠ taxable × rate',
  // Same arithmetic, different instruction: the one above says the producer
  // disagreed with itself, this one says your own edit contradicts the row.
  tax_math_after_correction: 'Your edit ≠ the rest of this row',
  uqc_missing: 'UQC needed',
  uqc_unknown_unit: 'Unknown unit',
  hsn_unrepairable: 'HSN unrepairable',
  // Valid, and still not reportable: a 4-digit heading where this
  // taxpayer's turnover band requires six (X-09).
  hsn_heading_only: 'HSN too short to report',
  // A code was confirmed for this product before, and it does not sit
  // under the heading this line carries. Never swapped silently.
  hsn_memory_disagrees: 'Confirmed code disagrees',
  // W3-12 (4a): the client's own HSN summary gives a code GSTN does not publish. The
  // detail beside the chip names it and the directory's codes under it to pick from.
  hsn_client_code_invalid: "Client's code is not a valid HSN/SAC",
  state_code_mismatch: 'State ≠ GSTIN prefix',
  // FR-E6: the GST portal rejected this row on upload. Its own message is shown beside the
  // chip (`rejected_by`); a correction after the rejection releases the hold.
  portal_rejected: 'Rejected by the GST portal',
  // A trial balance's line (W3-17 (8c)): the panel above the rows says each in full, from the
  // service's own words.
  tb_no_remark: 'No GST remark in the trial balance',
  tb_no_rate: 'No rate in the trial balance',
  tb_code_shared: 'Its code is on two rows',
  tb_shared_report: 'One sheet reports several rows',
  tb_unlinked_report: 'A sheet of the month is not linked',
  tb_report_awaits: 'A document it is less of awaits approval',
}

/**
 * What a row's 2B standing says, and how it reads.
 *
 * Green is "the portal agrees", amber is "a person has something to do" — the
 * same vocabulary the check chips use, so a matched row and a clean row look
 * alike on purpose.
 *
 * `held` is deliberately NOT amber-as-a-problem-with-the-2B: nothing was
 * compared, because a row that fails its own checks never reaches the matcher
 * (`recon/books.py`). Saying "not in GSTR-2B" there would be a claim about a
 * comparison nobody made. It reads as the sequence it is — clear the hold, and
 * the row gets compared.
 */
const RECON_CHIPS: Record<string, { label: string; tone: 'ok' | 'hold' }> = {
  matched: { label: 'matched in 2B', tone: 'ok' },
  unmatched: { label: 'not in GSTR-2B', tone: 'hold' },
  held: { label: 'not yet matched — row is held', tone: 'hold' },
  // The mirror, on the 2B's own rows: the portal has it, the books do not.
  // This is money — ITC the client is entitled to and has not claimed —
  // so it names the remedy rather than the absence.
  missing_in_books: { label: 'missing input — not booked', tone: 'hold' },
}

/** A fuzzy match is a PROPOSAL until someone accepts it (D-16). */
const RECON_SUGGESTED = 'match suggested — confirm on Reconciliation'

/**
 * The row's background, from its 2B standing. Empty string when the service
 * said nothing about this row — a table with no 2B imported is unchanged.
 *
 * A `suggested` match is amber, not green: it is a proposal awaiting a person,
 * and a green row would say the reconciliation is settled when it is not.
 */
function reconRowClass(state: ReviewReconRow | undefined): string {
  if (!state) return ''
  if (state.state === 'matched') {
    return state.status === 'suggested' ? 'is-recon-open' : 'is-recon-matched'
  }
  return 'is-recon-open'
}

/**
 * What a held document may be reclassified as, given its direction.
 *
 * This was the literal `['sales_report', 'invoice']` — and a purchase register
 * held at `review_extraction` had no way out, because neither option is what
 * it is. Found 2026-09-17 on a real Tally register: the engine was unsure, the
 * hold said "reclassify and resubmit", and the only buttons offered would have
 * filed 14 purchases as sales or collapsed the register into one invoice. The
 * reviewer's only correct move was to do nothing, so the document sat held and
 * the books side stayed empty.
 *
 * `purchase_report` was always a valid type — `_REPORT_DOC_TYPES` in
 * `ingestion/pipeline.py` routes it to mapping review exactly as
 * `sales_report` is routed. Only this list omitted it.
 *
 * The upload SLOT leads, because that is the thing a person already asserted
 * about this file: a register uploaded to the purchase slot should not be
 * offered "as sales report" first. Both registers stay offered — a slot can be
 * wrong, and that is exactly what reclassify is for.
 *
 * Read from `source_lane` rather than the document's `direction`, which the
 * documents endpoint does not serialise. The mapping is the service's
 * (`ingestion/direction.py`), and only the two books slots matter here.
 */
const _PURCHASE_LANES = new Set(['purchase_invoices'])

/** The reclassify buttons' words: "as a purchase register". */
const RECLASSIFY_AS: Record<string, string> = {
  purchase_report: 'a purchase register',
  sales_report: 'a sales register',
  invoice: 'an invoice',
}

function reclassifyKinds(lane: string | null | undefined): string[] {
  const registers = _PURCHASE_LANES.has(lane ?? '')
    ? ['purchase_report', 'sales_report']
    : ['sales_report', 'purchase_report']
  return [...registers, 'invoice']
}

/**
 * The verdict chip. The legacy's colour semantics — green ready, amber needs a
 * human, red blocking — over the service's own words, which are never
 * rewritten into a bucket.
 */
function chip(text: string, tone: 'hold' | 'ok' | 'conflict') {
  const tones = { ok: 'is-ok', hold: 'is-hold', conflict: 'is-stop' }
  return <span className={`gst-chip ${tones[tone]}`}>{text}</span>
}

export function Review() {
  const client = useClient()
  const { can } = useEvaluation()

  const [queue, setQueue] = useState<DocumentRecord[]>([])
  /** Documents kept out of the queue until approved (W3-13 (3c), 3d). */
  const [invoicesWaiting, setInvoicesWaiting] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const [body, setBody] = useState<ReviewBody | null>(null)
  // Which page of rows is on screen. Reset whenever the document does —
  // page 9 of a 40,568-line register means nothing in a 12-line one.
  const [offset, setOffset] = useState(0)
  const PAGE = 200
  const [error, setError] = useState<string | null>(null)
  const [correcting, setCorrecting] = useState<{ key: string; field: string; value: string } | null>(
    null,
  )
  const [reclassifying, setReclassifying] = useState(false)
  /** The layout that read this file, open for correcting (D-43). */
  const [rebinding, setRebinding] = useState(false)
  const [remark, setRemark] = useState<string | null>(null)
  const [money, setMoney] = useState<{
    key: string
    values: Record<string, string>
    reason: string
    note: string
  } | null>(null)
  const [excluding, setExcluding] = useState<{
    key: string
    reason: string
    note: string
  } | null>(null)
  // D-79: a row raised with the client — what they are asked to confirm.
  const [raising, setRaising] = useState<{ key: string; note: string } | null>(null)
  // D-123: a row dated in another month, being included in this one with a reason.
  const [including, setIncluding] = useState<{ key: string; note: string } | null>(null)
  // D-151: a foreign-currency invoice being converted at a rate a person types, with a reason.
  const [converting, setConverting] = useState<{
    key: string
    rate: string
    reason: string
    note: string
  } | null>(null)
  // Who typed a rate, in words: the platform's member list, read once and kept nowhere (N32).
  const [members, setMembers] = useState<Member[] | null>(null)
  const me = session.useSession()?.userId ?? null
  const [adding, setAdding] = useState<{
    values: Record<string, string>
    reason: string
    note: string
  } | null>(null)
  const [sourceOpen, setSourceOpen] = useState(false)
  /**
   * Gather the page's rows under one heading per supplier. ON by default,
   * because a register read supplier-by-supplier is the reason this screen
   * exists; OFF gives back the flat table byte for byte, since the grouping
   * reads `body.rows` and never mutates it.
   */
  const [grouped, setGrouped] = useState(true)
  /**
   * The groups a person has FOLDED SHUT, never the ones they have opened.
   *
   * Stored that way round so the default is everything open: a row visible
   * before this feature existed is still visible after it, and an empty set can
   * never hide anything.
   */
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  /** Which suggestion group's panel is open, by its group key. */
  const [suggestOpen, setSuggestOpen] = useState<string | null>(null)
  /** The buyer GSTIN typed into an open B2C group, before it is applied. */
  const [buyerGstin, setBuyerGstin] = useState('')
  /**
   * The minted capability. Held in state and NEVER in an href on load — it is
   * a bearer URL with no tenant check behind it, live for five minutes.
   */
  const [source, setSource] = useState<{ url: string; render: string } | null>(null)
  const [sourceError, setSourceError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    void fetchMembers(getOrgId()).then((found) => {
      if (live) setMembers(found)
    })
    return () => {
      live = false
    }
  }, [])

  const loadQueue = useCallback(async () => {
    try {
      const all = (await documents.list({ client_id: client.id })).data
      // The queue: anything a reviewer can act on — staged for review work,
      // or held for extraction review. One list, no silent bucket — except the
      // documents waiting for approval, which are approved in the preview and
      // come here once approved (W3-13 (3c), 3d); the count below says so.
      const { queue: reviewable, waiting } = rawDataQueue(all)
      setInvoicesWaiting(waiting)
      setQueue(reviewable)
      setSelected((current) => current ?? reviewable[0]?.id ?? null)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The review queue could not be loaded.')
    }
  }, [client.id])

  const loadDetail = useCallback(async () => {
    if (!selected) {
      setBody(null)
      return
    }
    try {
      setBody((await review.get(selected, { limit: PAGE, offset })).data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The document could not be loaded.')
    }
  }, [selected, offset])

  useEffect(() => {
    void loadQueue()
  }, [loadQueue])
  useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  // Close the remark box when the selection moves. A half-typed sentence about
  // one invoice must not be left sitting over the next one.
  useEffect(() => {
    setRemark(null)
    setMoney(null)
    setExcluding(null)
    setAdding(null)
    setRebinding(false)
  }, [selected])

  /**
   * Fold every group back open when the page of rows changes.
   *
   * The collapsed set holds keys built from THIS page's supplier names and
   * GSTINs. Carried to another document — or another page of the same one —
   * those keys either match nothing, or match a different supplier who happens
   * to share a name and registration, and a reviewer arrives at a register with
   * rows already hidden that they never folded away. Paging is included for
   * that second reason: a stale key that matches is worse than one that misses.
   */
  useEffect(() => {
    setCollapsed(new Set())
  }, [selected, offset])

  /**
   * Drop the previous document's URL the instant the selection moves. A stale
   * capability still renders — it would show the PREVIOUS invoice in a pane
   * captioned with this one's name, which is the worst bug this feature can
   * have. Clearing first and minting second makes that impossible.
   */
  useEffect(() => {
    setSource(null)
    setSourceError(null)
  }, [selected])

  const openSource = useCallback(async () => {
    if (!selected) return
    try {
      // The bytes, from ProGST itself, shown from a local copy that is freed when the
      // selection moves (the effect below).
      const file = await documents.sourceFile(selected)
      setSource({ url: URL.createObjectURL(file.blob), render: file.render ?? 'none' })
      setSourceError(null)
    } catch (err) {
      setSource(null)
      setSourceError(
        err instanceof ApiError ? err.message : 'The source could not be opened.',
      )
    }
  }, [selected])

  /**
   * Mint when the pane is open and there is nothing to show — on opening it,
   * and after a selection change cleared the last one.
   *
   * Deliberately NOT on a timer. The URL expires in five minutes; refreshing it
   * unattended would mint capabilities and write "viewed this invoice" audit
   * rows all afternoon for a tab nobody is looking at. Expiry shows a Reload.
   */
  useEffect(() => {
    if (sourceOpen && !source && !sourceError) void openSource()
  }, [sourceOpen, source, sourceError, openSource])

  // A shown document is a local copy; free it when it is replaced or the screen closes.
  useEffect(() => () => {
    if (source) URL.revokeObjectURL(source.url)
  }, [source])

  const selectedDoc = useMemo(
    () => queue.find((d) => d.id === selected) ?? null,
    [queue, selected],
  )

  /**
   * The page's rows under one heading per supplier — name AND GSTIN, never
   * name alone, and never two rows merged on a SHARED ABSENCE of a GSTIN. The
   * rule lives in `groupBySupplier`; this is only where it is read.
   *
   * A view, and nothing more. Each row keeps its own verdict, its own chips and
   * its own actions, because the same `<tr>` renders inside the group as
   * outside it, keyed by the same `content_key`.
   */
  // N30: by the party the register names — a sales register by buyer — and not at all for a
  // document that names neither.
  const party = partyOf(body?.direction)
  const supplierGroups = useMemo(
    () => (party ? groupByParty(body?.rows ?? [], party) : []),
    [body?.rows, party],
  )
  const words = party ? groupingWords(party, supplierGroups.length) : null

  /**
   * One entry per DISTINCT suggestion, not one per row.
   *
   * A 40,568-line register where every line lacks a buyer GSTIN produced
   * 40,568 identical "supply_type → B2C" lines, each with its own Accept —
   * a wall nobody reads and nobody could clear by hand. The same advice,
   * whatever number of rows it lands on, is ONE decision; the count is what
   * tells the reviewer how much rides on it. The rows themselves are still
   * carried, because acceptance is recorded per row (D-16) — grouping is a
   * display of the decision, never a change to what gets written.
   */
  const suggestionGroups = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; sample: ReviewSuggestion; items: ReviewSuggestion[] }
    >()
    for (const s of body?.suggestions ?? []) {
      // D-131: a split's value is an object; it groups by its rule (`suggestionGroupKey`).
      const key = suggestionGroupKey(s)
      const found = groups.get(key)
      if (found) found.items.push(s)
      else groups.set(key, { key, sample: s, items: [s] })
    }
    return [...groups.values()]
  }, [body?.suggestions])

  /**
   * The B2C suggestion is the one case where the advice has an alternative
   * worth typing: the rows are B2C only BECAUSE no buyer GSTIN was read, so
   * supplying one answers the whole group instead of accepting it.
   */
  const isB2cGroup = useCallback(
    (g: { sample: ReviewSuggestion }) =>
      g.sample.field === 'supply_type' && String(g.sample.value).toUpperCase() === 'B2C',
    [],
  )

  // A half-typed GSTIN must never survive a change of document or of group —
  // it would be applied to rows it was never read off.
  useEffect(() => {
    setSuggestOpen(null)
    setBuyerGstin('')
  }, [selected])

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setError(null)
      try {
        await fn()
        await Promise.all([loadDetail(), loadQueue()])
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That action failed.')
      }
    },
    [loadDetail, loadQueue],
  )

  /**
   * One row's acceptance. Unchanged in what it posts — the grouping above
   * only decides how many of these one press sends, never what each says.
   */
  const acceptOne = useCallback(
    (documentId: string, s: ReviewSuggestion, rows: ReviewRow[]) =>
      review.accept(documentId, {
        content_key: s.content_key,
        field: s.field,
        value: s.value,
        rule_id: s.rule_id,
        kind: s.kind === 'classification' ? 'classification' : 'enrichment',
        computed: s.computed,
        hsn:
          s.field === 'uqc'
            ? (rows.find((r) => r.content_key === s.content_key)?.effective.hsn as
                | string
                | undefined)
            : undefined,
        // The mirror of `hsn` above: a UQC acceptance teaches a master keyed
        // by HSN, an HSN acceptance teaches one keyed by the product.
        item:
          s.field === 'hsn'
            ? (rows.find((r) => r.content_key === s.content_key)?.effective.item as
                | string
                | undefined)
            : undefined,
      }),
    [],
  )

  /**
   * ONE invoice row, rendered the same whether it sits under a supplier
   * heading or in the flat table.
   *
   * Defined once and called from both, never copied. Every verdict chip and
   * every action below is keyed on `row.content_key`; a second near-identical
   * copy is what would eventually let a fix land on one and not the other, and
   * a `Take it out` that posts the wrong key is a row silently kept in — or
   * dropped from — somebody's return.
   *
   * `i` drives the zebra banding only. It is a position on screen, never an
   * identity: the key is the content key, so React reuses the right row and an
   * open correction box cannot jump to a neighbour when the grouping changes.
   */
  const invoiceRow = (
    row: ReviewRow,
    i: number,
    body: ReviewBody,
    selectedDoc: DocumentRecord,
  ) => (
      <tr
        key={row.content_key}
        className={[
          i % 2 === 1 ? 'is-a' : '',
          // The row's 2B standing, as a background. Only ever set when the
          // service said something about this row — with no 2B imported there
          // is no `recon.rows` entry and the table looks exactly as it did.
          reconRowClass(body.recon?.rows?.[row.content_key]),
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <td className="is-text is-sec">
          {String(row.effective.invoice_no ?? '—')}
          <small>{String(row.effective.invoice_date ?? '')}</small>
        </td>
        {/*
          * Each party on its OWN side, name beside GSTIN,
          * and no column that changes meaning with the
          * document — a header reading "Supplier" over one
          * file and "Buyer" over the next is how a reviewer
          * comes to trust a side that was never checked.
          *
          * A register states one party and the client is the
          * other, so a whole column of em dashes is ORDINARY
          * here. What is not ordinary is a party appearing
          * under the wrong one: on a purchase register the
          * supplier's name and GSTIN belong on the supplier
          * side, and seeing them under Buyer is the signal
          * that the row was sided wrong upstream, not a
          * display quirk to be papered over here.
          */}
        <td className="is-text">
          {String(row.effective.supplier_name ?? '—')}
        </td>
        <td className="is-text gst-mono">
          {String(row.effective.gstin_supplier ?? '—')}
        </td>
        <td className="is-text">
          {String(row.effective.buyer_name ?? '—')}
        </td>
        <td className="is-text gst-mono">
          {String(row.effective.gstin_buyer ?? '—')}
        </td>
        <td>
          {typeof row.effective.taxable === 'number'
            ? (row.effective.taxable / 100).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '—'}
        </td>
        <td className="is-text">
          <span className="flex flex-wrap items-center gap-1">
            {row.extracted.from_trial_balance ? (
              <span className="gst-tag">From the trial balance</span>
            ) : null}
            {row.accepted &&
              !row.needs_place_of_supply &&
              !row.invoice_type_hold &&
              !row.needs_inr_value &&
              chip('clean', 'ok')}
            {/* D-151: GSTR-1 holds a foreign-currency sale until something gives its rupees. */}
            {row.needs_inr_value && chip('Needs a rupee value', 'hold')}
            {/* D-144: GSTR-1 holds this row for it, so this screen says so too. */}
            {row.needs_place_of_supply && chip('No place of supply', 'hold')}
            {/* D-148: the invoice type the register states, and the hold GSTR-1 raises for it
                when it does not fit the row, naming both sides in the service's words. */}
            {invoiceTypeWords(row.effective.invoice_type) && (
              <span className="gst-tag">{invoiceTypeWords(row.effective.invoice_type)}</span>
            )}
            {row.invoice_type_hold && chip('Invoice type does not fit', 'hold')}
            {row.invoice_type_hold && (
              <span className="text-xs text-muted-foreground">{row.invoice_type_hold.why}</span>
            )}
            {/* D-148: the value in the invoice's own currency — shown, never added as rupees. */}
            {foreignValueWords(row.effective.currency, row.effective.fc_value) && (
              <span className="gst-tag">
                {foreignValueWords(row.effective.currency, row.effective.fc_value)}
              </span>
            )}
            {row.remember_sez && can(CLIENT, 'can_update') && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                title="Adds the buyer to the client's list of SEZ buyers, on your own word"
                onClick={() =>
                  void act(() =>
                    exportFacts.confirmSez(client.id, {
                      gstin: String(row.effective.gstin_buyer ?? ''),
                      derived_from: 'person',
                    }),
                  )
                }
              >
                Remember this buyer as SEZ
              </Button>
            )}
            {row.failing.map((name) =>
              chip(CHIP_LABELS[name] ?? name, 'hold'),
            )}
            {row.conflicts.map((c, i) => (
              <span key={i}>{chip(`conflict: ${String(c.field)}`, 'conflict')}</span>
            ))}
            {body.flags
              .filter((f) => f.content_key === row.content_key)
              .map((f, i) => (
                <span key={`${f.reason}-${i}`} className="inline-flex flex-wrap items-center gap-1">
                  {chip(CHIP_LABELS[f.reason] ?? f.reason, 'hold')}
                  {f.detail && (
                    <span className="text-xs text-muted-foreground">{f.detail}</span>
                  )}
                  {can(DOCUMENT, 'can_update') &&
                    flagChoices(f).map((choice) => (
                      <Button
                        key={choice.code}
                        type="button"
                        variant="outline"
                        size="sm"
                        title={choice.description}
                        onClick={() =>
                          void act(() =>
                            review.correct(selectedDoc.id, choiceCorrection(f, choice)),
                          )
                        }
                      >
                        {choiceLabel(choice)}
                      </Button>
                    ))}
                </span>
              ))}
            {(() => {
              const state = body.recon?.rows?.[row.content_key]
              if (!state) return null
              const spec = RECON_CHIPS[state.state]
              if (!spec) return null
              // A fuzzy match is born `suggested` and only a recorded
              // acceptance moves it (D-16), so it must not read as settled.
              const suggested = state.status === 'suggested'
              return chip(
                suggested ? RECON_SUGGESTED : spec.label,
                suggested ? 'hold' : spec.tone,
              )
            })()}
            {row.excluded_by && chip('removed by you', 'conflict')}
            {row.added_by && chip('added by you', 'ok')}
            {row.raised_by && chip('raised with client', 'hold')}
            {row.rejected_by && (
              <span className="text-xs text-muted-foreground">
                The portal said: {row.rejected_by.error_msg ?? 'no message'}
                {row.rejected_by.error_cd ? ` (${row.rejected_by.error_cd})` : ''} — correct the
                row and upload again
              </span>
            )}
            {/* D-123: a row dated in another month is left out of this one unless included. */}
            {row.outside_month &&
              !row.included_by &&
              chip(`dated ${row.outside_month}, left out of this month`, 'hold')}
            {row.included_by && chip(`included from ${row.included_by.month}`, 'ok')}
            {/* D-124: a cancelled bill is counted in Table 13 and booked nowhere. */}
            {row.cancelled && chip('cancelled bill', 'ok')}
            {/* D-131: a bill split into its taxed part and an exempt part, and that exempt part. */}
            {row.split_by && chip('split bill', 'ok')}
            {row.split_from && chip('untaxed part of a split bill', 'ok')}
            {/* D-49: a bill-wise register's tax, rounded item by item, passed
                within a rupee — and says by how much. */}
            {typeof row.effective.tax_rounding === 'number' &&
              chip(taxRoundingWords(row.effective.tax_rounding), 'ok')}
          </span>
          {/*
            * An excluded row stays in this table on purpose.
            * If the screen showed only live rows there would
            * be no way back, and an append-only design would
            * be undone by an interface with no undo.
            */}
          {row.excluded_by && (
            <p className="gst-meta mt-1">
              {row.excluded_by.note}{' '}
              {can(DOCUMENT, 'can_update') && (
                <button
                  type="button"
                  className="gst-link"
                  onClick={() =>
                    void act(() =>
                      review.exclude(selectedDoc.id, {
                        content_key: row.content_key,
                        excluded: false,
                        reason_code: 'other',
                        note: 'Put back into the return.',
                      }),
                    )
                  }
                >
                  Put it back
                </button>
              )}
            </p>
          )}
          {/*
            * D-79: a raised row stays in the return. Its question is shown where the
            * preparer works, with the way back beside it.
            */}
          {row.raised_by && (
            <p className="gst-meta mt-1">
              To confirm with the client: {row.raised_by.note}{' '}
              {can(DOCUMENT, 'can_update') && (
                <button
                  type="button"
                  className="gst-link"
                  onClick={() =>
                    void act(() =>
                      review.raiseWithClient(selectedDoc.id, {
                        content_key: row.content_key,
                        raised: false,
                        note: '',
                      }),
                    )
                  }
                >
                  Withdraw
                </button>
              )}
            </p>
          )}
          {/*
            * D-131: what a split did to the bill, with the way back on the bill's own row. Its
            * untaxed part is raised with the client, so its question shows on that row above.
            */}
          {row.split_by && (
            <p className="gst-meta mt-1">
              {splitWords(row.split_by)}{' '}
              {can(DOCUMENT, 'can_update') && (
                <button
                  type="button"
                  className="gst-link"
                  onClick={() =>
                    void act(() =>
                      review.splitBill(selectedDoc.id, {
                        content_key: row.content_key,
                        split: false,
                      }),
                    )
                  }
                >
                  Undo the split
                </button>
              )}
            </p>
          )}
          {/*
            * D-151: the rate a person typed, who typed it and why, with the way back. Undoing it
            * on this row undoes it on every row of the invoice.
            */}
          {row.converted_by && (
            <p className="gst-meta mt-1">
              {conversionWords(
                row.converted_by,
                row.effective,
                body.rate_reasons ?? {},
                rateTypedBy(row.converted_by, members, me),
              )}{' '}
              {can(DOCUMENT, 'can_update') && (
                <button
                  type="button"
                  className="gst-link"
                  onClick={() =>
                    void act(() =>
                      review.convert(selectedDoc.id, {
                        content_key: row.content_key,
                        converted: false,
                      }),
                    )
                  }
                >
                  Undo the conversion
                </button>
              )}
            </p>
          )}
          {/*
            * D-123: why an earlier month's row is in this one, and — when it was included
            * here rather than with its whole document — the way back.
            */}
          {row.included_by && (
            <p className="gst-meta mt-1">
              Included from {row.included_by.month}: {row.included_by.note}
              {row.included_by.by === 'document' && ' (the whole document was included)'}{' '}
              {row.included_by.by === 'row' && can(DOCUMENT, 'can_update') && (
                <button
                  type="button"
                  className="gst-link"
                  onClick={() =>
                    void act(() =>
                      review.includeInMonth(selectedDoc.id, {
                        content_key: row.content_key,
                        included: false,
                        note: '',
                      }),
                    )
                  }
                >
                  Leave it out again
                </button>
              )}
            </p>
          )}
          {can(DOCUMENT, 'can_update') &&
            !row.excluded_by &&
            money?.key !== row.content_key &&
            excluding?.key !== row.content_key &&
            raising?.key !== row.content_key &&
            including?.key !== row.content_key &&
            converting?.key !== row.content_key && (
              <span className="mt-1 flex gap-2">
                <button
                  type="button"
                  className="gst-link"
                  onClick={() =>
                    setMoney({
                      key: row.content_key,
                      values: {},
                      reason: '',
                      note: '',
                    })
                  }
                >
                  Correct amounts
                </button>
                <button
                  type="button"
                  className="gst-link"
                  onClick={() =>
                    setExcluding({
                      key: row.content_key,
                      reason: '',
                      note: '',
                    })
                  }
                >
                  Take it out
                </button>
                {!row.raised_by && (
                  <button
                    type="button"
                    className="gst-link"
                    onClick={() => setRaising({ key: row.content_key, note: '' })}
                  >
                    Raise with client
                  </button>
                )}
                {row.outside_month &&
                  !row.included_by &&
                  !_PURCHASE_LANES.has(selectedDoc.source_lane ?? '') && (
                    <button
                      type="button"
                      className="gst-link"
                      onClick={() => setIncluding({ key: row.content_key, note: '' })}
                    >
                      Include in this month
                    </button>
                  )}
                {offersConversion(row) && (
                  <button
                    type="button"
                    className="gst-link"
                    onClick={() =>
                      setConverting({ key: row.content_key, rate: '', reason: '', note: '' })
                    }
                  >
                    Convert at a rate…
                  </button>
                )}
              </span>
            )}
          {converting?.key === row.content_key && (
            <Card className="mt-2 flex flex-wrap items-center gap-2 p-2">
              <label className="gst-meta">
                {rateLabel(row.effective.currency)}{' '}
                <input
                  value={converting.rate}
                  onChange={(e) =>
                    setConverting({ ...converting, rate: e.target.value.slice(0, 32) })
                  }
                  placeholder="e.g. 83.2150"
                  inputMode="decimal"
                  className="gst-input w-28"
                />
              </label>
              <select
                value={converting.reason}
                onChange={(e) => setConverting({ ...converting, reason: e.target.value })}
                aria-label="Why this rate"
                className="gst-select"
              >
                <option value="">Why this rate?</option>
                {rateChoices(body).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                value={converting.note}
                onChange={(e) =>
                  setConverting({ ...converting, note: e.target.value.slice(0, 500) })
                }
                placeholder={
                  converting.reason === 'other'
                    ? 'Where the rate came from (needed)'
                    : 'A note (optional)'
                }
                className="gst-input min-w-56 flex-1"
              />
              <Button
                type="button"
                disabled={
                  !converting.rate.trim() ||
                  !converting.reason ||
                  (converting.reason === 'other' && !converting.note.trim())
                }
                onClick={() =>
                  void act(async () => {
                    await review.convert(selectedDoc.id, {
                      content_key: row.content_key,
                      converted: true,
                      rate: converting.rate.trim(),
                      reason_code: converting.reason,
                      note: converting.note,
                    })
                    setConverting(null)
                  })
                }
                variant="outline" size="sm"
              >
                Convert the invoice
              </Button>
              <Button
                type="button"
                variant="outline" size="sm"
                onClick={() => setConverting(null)}
              >
                Cancel
              </Button>
            </Card>
          )}
          {including?.key === row.content_key && (
            <Card className="mt-2 flex flex-wrap items-center gap-2 p-2">
              <input
                value={including.note}
                onChange={(e) =>
                  setIncluding({ ...including, note: e.target.value.slice(0, 500) })
                }
                placeholder={`Why is this ${row.outside_month} invoice in this month? e.g. missed then`}
                className="gst-input min-w-56 flex-1"
              />
              <Button
                type="button"
                disabled={!including.note.trim()}
                onClick={() =>
                  void act(async () => {
                    await review.includeInMonth(selectedDoc.id, {
                      content_key: row.content_key,
                      included: true,
                      note: including.note,
                    })
                    setIncluding(null)
                  })
                }
                variant="outline" size="sm"
              >
                Include it in this month
              </Button>
              <Button
                type="button"
                variant="outline" size="sm"
                onClick={() => setIncluding(null)}
              >
                Cancel
              </Button>
            </Card>
          )}
          {raising?.key === row.content_key && (
            <Card className="mt-2 flex flex-wrap items-center gap-2 p-2">
              <input
                value={raising.note}
                onChange={(e) =>
                  setRaising({ ...raising, note: e.target.value.slice(0, 500) })
                }
                placeholder="What should the client confirm? They read this on their workbook"
                className="gst-input min-w-56 flex-1"
              />
              <Button
                type="button"
                disabled={!raising.note.trim()}
                onClick={() =>
                  void act(async () => {
                    await review.raiseWithClient(selectedDoc.id, {
                      content_key: row.content_key,
                      raised: true,
                      note: raising.note,
                    })
                    setRaising(null)
                  })
                }
                variant="outline" size="sm"
              >
                {/* The confirm says what it does; the link that opened the form asked. */}
                Raise it with the client
              </Button>
              <Button
                type="button"
                variant="outline" size="sm"
                onClick={() => setRaising(null)}
              >
                Cancel
              </Button>
            </Card>
          )}
          {money?.key === row.content_key && (
            <Card className="mt-2 p-2">
              <div className="flex flex-wrap gap-2">
                {[...body.money_fields, ...body.rate_fields].map((f) => (
                  <label key={f} className="gst-meta">
                    {body.field_words[f] ?? f}
                    <input
                      value={money.values[f] ?? ''}
                      onChange={(e) =>
                        setMoney({
                          ...money,
                          values: {
                            ...money.values,
                            [f]: e.target.value,
                          },
                        })
                      }
                      placeholder={
                        typeof row.effective[f] === 'number' &&
                        body.money_fields.includes(f)
                          ? String((row.effective[f] as number) / 100)
                          : String(row.effective[f] ?? '')
                      }
                      className="gst-input w-24"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <select
                  value={money.reason}
                  onChange={(e) =>
                    setMoney({ ...money, reason: e.target.value })
                  }
                  className="gst-select"
                >
                  <option value="">Why?</option>
                  {Object.entries(body.money_reasons).map(
                    ([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ),
                  )}
                </select>
                <input
                  value={money.note}
                  onChange={(e) =>
                    setMoney({ ...money, note: e.target.value.slice(0, 500) })
                  }
                  placeholder="What does the document say?"
                  className="gst-input min-w-56 flex-1"
                />
                <Button
                  type="button"
                  disabled={
                    !money.reason ||
                    !money.note.trim() ||
                    !Object.values(money.values).some((v) => v.trim())
                  }
                  onClick={() =>
                    void act(async () => {
                      await review.correctMoney(selectedDoc.id, {
                        content_key: row.content_key,
                        values: Object.fromEntries(
                          Object.entries(money.values).filter(
                            ([, v]) => v.trim(),
                          ),
                        ),
                        reason_code: money.reason,
                        note: money.note,
                      })
                      setMoney(null)
                    })
                  }
                  variant="outline" size="sm"
                >
                  Save amounts
                </Button>
                <Button
                  type="button"
                  variant="outline" size="sm"
                  onClick={() => setMoney(null)}
                >
                  Cancel
                </Button>
              </div>
            </Card>
          )}
          {excluding?.key === row.content_key && (
            <Card className="mt-2 flex flex-wrap items-center gap-2 p-2">
              <select
                value={excluding.reason}
                onChange={(e) =>
                  setExcluding({ ...excluding, reason: e.target.value })
                }
                className="gst-select"
              >
                <option value="">Why?</option>
                {/* N34: why a row comes out, not why an amount was corrected. */}
                {excludeChoices(body).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                value={excluding.note}
                onChange={(e) =>
                  setExcluding({
                    ...excluding,
                    note: e.target.value.slice(0, 500),
                  })
                }
                placeholder="The client will see this on the workbook"
                className="gst-input min-w-56 flex-1"
              />
              <Button
                type="button"
                disabled={!excluding.reason || !excluding.note.trim()}
                onClick={() =>
                  void act(async () => {
                    await review.exclude(selectedDoc.id, {
                      content_key: row.content_key,
                      excluded: true,
                      reason_code: excluding.reason,
                      note: excluding.note,
                    })
                    setExcluding(null)
                  })
                }
                variant="outline" size="sm"
              >
                {/*
                  * NOT "Take it out" again. That is the
                  * label on the button that OPENED this form,
                  * so the same words appeared twice for two
                  * different acts — one that asks and one
                  * that does. The confirm says what it
                  * confirms.
                  */}
                Remove this row
              </Button>
              <Button
                type="button"
                variant="outline" size="sm"
                onClick={() => setExcluding(null)}
              >
                Cancel
              </Button>
            </Card>
          )}
          {offersCorrection(row) && can(DOCUMENT, 'can_update') && (
            <span className="mt-1 block">
              {correcting?.key === row.content_key ? (
                <span className="flex flex-wrap items-center gap-1">
                  <select
                    value={correcting.field}
                    onChange={(e) =>
                      setCorrecting({ ...correcting, field: e.target.value })
                    }
                    className="gst-select"
                  >
                    {(body.correctable_fields ?? []).map((f) => (
                      <option key={f} value={f}>
                        {body.field_words[f] ?? f}
                      </option>
                    ))}
                  </select>
                  <input
                    value={correcting.value}
                    onChange={(e) =>
                      setCorrecting({ ...correcting, value: e.target.value })
                    }
                    placeholder={
                      correcting.field === 'place_of_supply'
                        ? 'a state, e.g. 29-Karnataka'
                        : correcting.field === 'invoice_type'
                          ? 'e.g. SEZ supply with payment'
                          : 'new value'
                    }
                    // D-144: a place of supply is picked from the states; a place outside
                    // India can still be typed, for an export. D-148: an invoice type from six.
                    list={
                      correcting.field === 'place_of_supply'
                        ? 'places-of-supply'
                        : correcting.field === 'invoice_type'
                          ? 'invoice-types'
                          : undefined
                    }
                    aria-label={body.field_words[correcting.field] ?? correcting.field}
                    className="gst-input w-36"
                  />
                  {correcting.field === 'place_of_supply' && (
                    <datalist id="places-of-supply">
                      {(body.places_of_supply ?? []).map((place) => (
                        <option key={place} value={place} />
                      ))}
                    </datalist>
                  )}
                  {correcting.field === 'invoice_type' && (
                    <datalist id="invoice-types">
                      {Object.values(body.invoice_types ?? {}).map((words) => (
                        <option key={words} value={words} />
                      ))}
                    </datalist>
                  )}
                  <Button
                    type="button"
                    disabled={!correcting.value.trim()}
                    onClick={() =>
                      void act(() =>
                        review.correct(selectedDoc.id, {
                          content_key: row.content_key,
                          field: correcting.field,
                          value: correcting.value.trim(),
                        }),
                      ).then(() => setCorrecting(null))
                    }
                    variant="outline" size="sm"
                  >
                    Apply
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setCorrecting(null)}
                    variant="outline" size="sm"
                  >
                    Cancel
                  </Button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    setCorrecting({
                      key: row.content_key,
                      field: correctionFieldFor(row),
                      value: '',
                    })
                  }
                  className="gst-link"
                >
                  {row.accepted && row.is_export ? 'Add the shipping bill…' : 'Correct…'}
                </button>
              )}
            </span>
          )}
        </td>
      </tr>
  )

  return (
    <div>
      <header className="mb-6">
        <h1 className="gst-h1">
          <ListChecks size={20} /> Raw Data — review
        </h1>
        <p className="gst-sub">
          Rows hold with their failing checks named; a fix is what releases them
        </p>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      {/*
        * Client-scoped, so it sits above the per-document queue rather than
        * inside it: the sheet answers a product once for the whole taxpayer,
        * not once per register it happens to appear in.
        */}
      <HsnQueueCard
        clientId={client.id}
        gstin={client.gstin}
        period={selectedDoc?.period ?? null}
        onApplied={() => {
          void loadQueue()
          void loadDetail()
        }}
      />

      <div className="flex flex-wrap gap-6">
        {/* -- the queue ---------------------------------------------------- */}
        <aside className="w-64 shrink-0">
          {invoicesWaiting > 0 && (
            <Alert tone="warning" className="mb-2 text-sm">
              {rawDataWaitingWords(invoicesWaiting)}{' '}
              <Link to={clientPath(client.id, 'intake')}>Approve on Upload Data</Link>.
            </Alert>
          )}
          {queue.length === 0 ? (
            <div className="gst-empty">Nothing to review.</div>
          ) : (
            <ul className="space-y-1">
              {/* D-93: a workbook's sheets together under its name, each named by its sheet. */}
              {queueGroups(queue).map((group) => (
                <li key={group.key}>
                  {group.workbook && (
                    <p className="gst-meta truncate px-2 pt-1" title={group.workbook}>
                      {group.workbook}
                    </p>
                  )}
                  <ul className={`space-y-1${group.workbook ? ' pl-3' : ''}`}>
                    {group.docs.map((doc) => (
                      <li key={doc.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelected(doc.id)
                            setOffset(0)
                          }}
                          aria-current={doc.id === selected}
                          className="gst-rail-item"
                        >
                          <span className="block truncate">
                            {group.workbook ? doc.sheet_name : documentName(doc)}
                          </span>
                          <span className="mt-0.5 block text-[11px]">
                            {doc.held === 'review_extraction'
                              ? 'Review first'
                              : STAGE_LABELS[doc.stage ?? ''] ?? 'Booked'}
                            {' · '}
                            {doc.period}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* -- the detail --------------------------------------------------- */}
        {/*
          * `basis` is what keeps the table readable: with `min-w-0 flex-1` on
          * this and a flex-1 pane beside it, the SECTION is the side that
          * collapses — at 1440px it was squeezed to ~150px while the pane took
          * 560. It needs a basis to grow from, and the pane needs a fixed width
          * so it competes for nothing.
          */}
        <section className="min-w-0 flex-1 basis-[520px]">
          {!body || !selectedDoc ? (
            <p className="text-sm text-muted-foreground">Select a document.</p>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {documentName(selectedDoc)}
                  </p>
                  <p className="gst-meta">
                    {selectedDoc.doc_type_words} · read by {selectedDoc.read_by}
                    {selectedDoc.checksum_ok === false && " · the reader's own arithmetic check failed"}
                  </p>
                </div>
                {/*
                  * A document ingested before originals were retained has no
                  * source and never will — the de-duplication rule means a
                  * re-upload cannot supply one. Say that, rather than showing
                  * nothing: an absent button explains nothing, and "missing"
                  * would send someone hunting for a file that never existed.
                  */}
                {selectedDoc.source && !selectedDoc.source.available && (
                  <p className="gst-meta">
                    No source file was kept — uploaded before originals were retained.
                  </p>
                )}
                {selectedDoc.verified && (
                  <p className="gst-meta">
                    <BadgeCheck size={12} className="inline" /> Verified{' '}
                    {whenWords(selectedDoc.verified.at)} —{' '}
                    {selectedDoc.verified.remark}
                  </p>
                )}
                {can(DOCUMENT, 'can_view') && selectedDoc.source?.available && (
                  <Button
                    type="button"
                    onClick={() => setSourceOpen((v) => !v)}
                    variant="outline" size="sm"
                  >
                    {sourceOpen ? <EyeOff size={13} /> : <Eye size={13} />}
                    {sourceOpen ? 'Hide document' : 'Show document'}
                  </Button>
                )}
                {/*
                  * The suggestions, as ONE control between the document and
                  * the verification — the order a reviewer works in: look at
                  * the page, settle what the service advises, then verify.
                  */}
                {suggestionGroups.length > 0 && can(DOCUMENT, 'can_update') && (
                  <Button
                    type="button"
                    onClick={() => {
                      setSuggestOpen((k) => (k === null ? (suggestionGroups[0]?.key ?? null) : null))
                      setBuyerGstin('')
                    }}
                    variant="outline" size="sm"
                  >
                    <Lightbulb size={13} />
                    {suggestionGroups.length === 1
                      ? '1 suggestion'
                      : `${suggestionGroups.length} suggestions`}
                  </Button>
                )}
                {can(DOCUMENT, 'can_update') && remark === null && (
                  <Button type="button" onClick={() => setRemark('')} variant="outline" size="sm">
                    <BadgeCheck size={13} />
                    {selectedDoc.verified ? 'Verify again' : 'Verify'}
                  </Button>
                )}
                {/*
                  * Only where it can work (C4, 2026-09-28). This button was on
                  * every document while the running producer refused every
                  * reclassify — the one remedy on the screen that always failed.
                  */}
                {can(DOCUMENT, 'can_update') && body.can_reclassify && (
                  <div className="flex items-center gap-2">
                    {reclassifying ? (
                      <>
                        {reclassifyKinds(selectedDoc.source_lane).map((kind) => (
                          <Button
                            key={kind}
                            type="button"
                            onClick={() =>
                              void act(() => documents.reclassify(selectedDoc.id, { doc_type: kind }))
                            }
                            variant="outline" size="sm"
                          >
                            as {RECLASSIFY_AS[kind] ?? 'another kind of document'}
                          </Button>
                        ))}
                        <Button
                          type="button"
                          onClick={() => setReclassifying(false)}
                          variant="outline" size="sm"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => setReclassifying(true)}
                        variant="outline" size="sm"
                      >
                        <RefreshCw size={13} /> Reclassify & resubmit
                      </Button>
                    )}
                  </div>
                )}
                {/*
                  * N14 (UAT, 2026-09-30): a binding made once was never offered
                  * again, so a wrong one read every later file the same way.
                  * The service says whether this file was read through one.
                  */}
                {can(DOCUMENT, 'can_update') && body.can_rebind && !rebinding && (
                  <Button type="button" onClick={() => setRebinding(true)} variant="outline" size="sm">
                    <Columns3 size={13} /> Change the columns
                  </Button>
                )}
              </div>

              {belowTotalNote(body.register_totals?.below_total) && (
                <p className="mb-4 text-sm text-muted-foreground">
                  {belowTotalNote(body.register_totals?.below_total)}
                </p>
              )}

              {body.trial_balance && (
                <div className="mb-4">
                  <TrialBalancePane
                    tb={body.trial_balance}
                    clientId={client.id}
                    canEdit={can(DOCUMENT, 'can_update')}
                    onChanged={loadDetail}
                  />
                </div>
              )}

              {rebinding && (
                <ColumnMapper
                  documentId={selectedDoc.id}
                  clientId={client.id}
                  filename={selectedDoc.source_filename ?? 'this file'}
                  onClose={() => setRebinding(false)}
                  onApplied={(read) => {
                    // The file now has a new reading — a new document; the one
                    // on screen is superseded and leaves the queue.
                    setRebinding(false)
                    setSelected(read?.id ?? null)
                    void loadQueue()
                  }}
                />
              )}

              {/*
                * Suggestions (D-16: a person accepts, or nothing happens).
                *
                * ONE panel, opened from the button above, listing each
                * DISTINCT piece of advice once with the number of rows it
                * covers. Accepting still writes one acceptance per row — the
                * count in the heading is what makes the size of that visible
                * before it is pressed, which a wall of identical lines never
                * did.
                */}
              {suggestOpen !== null &&
                suggestionGroups.length > 0 &&
                can(DOCUMENT, 'can_update') && (
                  <Alert tone="warning" className="mb-4">
                    <p className="gst-panel-h">Suggestions — nothing applies until you accept it</p>
                    <ul className="space-y-2">
                      {suggestionGroups.map((g) => {
                        const n = g.items.length
                        const rowWord = n === 1 ? '1 row' : `${n.toLocaleString()} rows`
                        return (
                          <li key={g.key} className="text-sm text-foreground">
                            <div className="flex flex-wrap items-center gap-2">
                              {g.sample.field === SPLIT_FIELD ? (
                                <span>
                                  {splitGroupWords(g.sample.rule_id, n, g.sample.reason)}
                                </span>
                              ) : (
                              <span>
                                {body.field_words[g.sample.field] ?? 'A field'} →{' '}
                                <strong>{String(g.sample.value)}</strong>
                                <span className="text-xs text-muted-foreground">
                                  {' '}
                                  ({g.sample.reason}) · {rowWord}
                                </span>
                                {g.sample.computed && (
                                  <span className="text-xs text-muted-foreground"> · computed</span>
                                )}
                              </span>
                              )}
                              <Button
                                type="button"
                                onClick={() =>
                                  void act(async () => {
                                    for (const s of g.items) {
                                      await acceptOne(selectedDoc.id, s, body.rows)
                                    }
                                    setSuggestOpen(null)
                                    setBuyerGstin('')
                                  })
                                }
                                variant="outline" size="sm"
                              >
                                <Check size={12} /> Accept
                                {n > 1 && ` all ${n.toLocaleString()}`}
                              </Button>
                            </div>
                            {/*
                              * The alternative to accepting B2C: these rows are
                              * B2C only because no buyer GSTIN was read, so
                              * typing the one they all share answers the group
                              * without accepting advice that was drawn from the
                              * absence. Fifteen characters is the whole of a
                              * GSTIN — the field takes no more, and the service
                              * still checks the checksum.
                              */}
                            {isB2cGroup(g) && (
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <label className="gst-meta" htmlFor="buyer-gstin">
                                  or set the buyer GSTIN
                                </label>
                                <input
                                  id="buyer-gstin"
                                  value={buyerGstin}
                                  onChange={(e) =>
                                    setBuyerGstin(
                                      e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 15),
                                    )
                                  }
                                  maxLength={15}
                                  placeholder="15-character GSTIN"
                                  className="gst-input w-48 gst-mono"
                                />
                                <span className="gst-meta">{buyerGstin.length}/15</span>
                                <Button
                                  type="button"
                                  disabled={buyerGstin.length !== 15}
                                  onClick={() =>
                                    void act(async () => {
                                      for (const s of g.items) {
                                        await review.correct(selectedDoc.id, {
                                          content_key: s.content_key,
                                          field: 'gstin_buyer',
                                          value: buyerGstin,
                                        })
                                      }
                                      setSuggestOpen(null)
                                      setBuyerGstin('')
                                    })
                                  }
                                  variant="outline" size="sm"
                                >
                                  Apply to {rowWord}
                                </Button>
                              </div>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </Alert>
                )}

              {/*
                * The remark box. NOT pre-filled, and that is a deliberate
                * departure from the legacy, which pre-fills "I have reviewed
                * and verified this invoice." — a confirmation you satisfy by
                * pressing nothing is weaker than one you satisfy by pressing
                * the space bar, and the server refuses that one. Asking a
                * person to write what they checked is the entire value here.
                */}
              {remark !== null && (
                <Card className="mb-4 p-3">
                  <label className="gst-meta mb-1 block" htmlFor="verify-remark">
                    What did you check?
                  </label>
                  <textarea
                    id="verify-remark"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value.slice(0, 500))}
                    placeholder="e.g. Agrees with the invoice; buyer GSTIN corrected."
                    className="gst-input min-h-16 w-full"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      type="button"
                      disabled={!remark.trim()}
                      onClick={() =>
                        void act(async () => {
                          await review.verify(selectedDoc.id, { remark })
                          setRemark(null)
                        })
                      }
                      variant="outline" size="sm"
                    >
                      <Check size={13} /> Save &amp; verify
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setRemark(null)}
                      variant="outline" size="sm"
                    >
                      Cancel
                    </Button>
                    <span className="gst-meta">{500 - remark.length} left</span>
                  </div>
                </Card>
              )}

              {/*
                * What the whole document holds, above a page of it. These are
                * the service's tallies over every row, never a count of the
                * array below — paging that hid the scale would be worse than
                * the payload it was shrinking.
                */}
              {body.recon?.state === 'not_matched_yet' && (
                <p className="gst-meta mb-3">
                  GSTR-2B version {body.recon.import_version} is imported, and matching has not
                  been run on it yet — so no row here is marked as missing from it. Run matching on
                  Reconciliation.
                </p>
              )}
              {body.total_rows > body.rows.length && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="gst-meta">
                    Rows {body.offset + 1}&ndash;
                    {Math.min(body.offset + body.rows.length, body.total_rows)} of{' '}
                    {body.total_rows.toLocaleString()}
                  </span>
                  {Object.entries(body.flag_counts).map(([reason, n]) =>
                    chip(
                      `${n.toLocaleString()} ${CHIP_LABELS[reason] ?? reason}`,
                      'hold',
                    ),
                  )}
                  <span className="ml-auto flex gap-2">
                    <Button
                      type="button" variant="outline" size="sm"
                      disabled={body.offset === 0}
                      onClick={() => setOffset(Math.max(0, body.offset - PAGE))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button" variant="outline" size="sm"
                      disabled={body.offset + body.rows.length >= body.total_rows}
                      onClick={() => setOffset(body.offset + PAGE)}
                    >
                      Next
                    </Button>
                  </span>
                </div>
              )}

              {/*
                * Gathering by supplier, and the way back to the flat table.
                *
                * Its own row rather than a place in the paging strip above,
                * which renders only when a document is longer than one page —
                * the control has to be reachable on a twelve-line register too.
                */}
              {body.rows.length > 0 && words && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-pressed={grouped}
                    onClick={() => setGrouped((v) => !v)}
                  >
                    {grouped ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    {grouped ? words.on : words.off}
                  </Button>
                  {grouped && (
                    <>
                      <span className="gst-meta">
                        {words.count}
                      </span>
                      {/*
                        * Only offered once there is more than one heading to
                        * fold — on a single-supplier page it would be a control
                        * that hides the entire table and says so in the past
                        * tense.
                        */}
                      {supplierGroups.length > 1 && (
                        <button
                          type="button"
                          className="gst-link"
                          onClick={() =>
                            setCollapsed((current) =>
                              current.size === 0
                                ? new Set(supplierGroups.map((g) => g.key))
                                : new Set(),
                            )
                          }
                        >
                          {collapsed.size === 0 ? 'Collapse all' : 'Expand all'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* the rows, verdict-chipped */}
              {body.rows.length === 0 ? (
                <div className="gst-empty">
                  The extraction carries no rows —{' '}
                  {selectedDoc.held !== 'review_extraction'
                    ? 'nothing to review here.'
                    : body.can_reclassify
                      ? 'reclassify and resubmit, or send a readable copy.'
                      : 'send a readable copy.'}
                </div>
              ) : (
                <div className="gst-table-wrap">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Invoice</th>
                        <th className="is-text">Supplier</th>
                        <th className="is-text">Supplier GSTIN</th>
                        <th className="is-text">Buyer</th>
                        <th className="is-text">Buyer GSTIN</th>
                        <th>Taxable (₹)</th>
                        <th className="is-text">Verdict</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/*
                        * ONE renderer for the invoice row, called from both the
                        * grouped and the flat body below.
                        *
                        * Not two copies that look alike. Every verdict chip and
                        * every action in here is keyed on `row.content_key`, and
                        * a second copy is the thing that would eventually let a
                        * fix land on one and not the other — a `Take it out`
                        * that posts the wrong key is a row silently kept in, or
                        * dropped from, somebody's return.
                        */}
                      {(grouped && words
                          ? supplierGroups.flatMap((group, gi) => {
                              const shut = collapsed.has(group.key)
                              const n = group.rows.length
                              return [
                                <tr key={`group:${group.key}`} className="is-group">
                                  {/*
                                    * The heading spans the table and carries NO
                                    * verdict and NO action. A supplier-level
                                    * chip would be this screen inventing a
                                    * judgement the service never issued, and on
                                    * a supplier with four clean invoices and one
                                    * that fails, any single chip is false
                                    * (ADR-0014). Aggregates here are arithmetic
                                    * only: a count and a sum.
                                    */}
                                  <td className="is-text" colSpan={7}>
                                    <button
                                      type="button"
                                      className="gst-group-toggle"
                                      aria-expanded={!shut}
                                      onClick={() =>
                                        setCollapsed((current) => {
                                          const next = new Set(current)
                                          if (!next.delete(group.key)) next.add(group.key)
                                          return next
                                        })
                                      }
                                    >
                                      {shut ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                      <span>{groupHeading(group).name}</span>
                                      {groupHeading(group).gstin && (
                                        <span className="gst-group-gstin">
                                          GSTIN: {groupHeading(group).gstin}
                                        </span>
                                      )}
                                      {/*
                                        * "on this page", said out loud. The
                                        * service pages rows at 200, so a
                                        * supplier can straddle a boundary and an
                                        * unqualified total would read as the
                                        * whole document's — wrong by omission on
                                        * any register longer than a page.
                                        */}
                                      <span className="gst-group-tally">
                                        {n === 1 ? '1 invoice' : `${n.toLocaleString()} invoices`} on
                                        this page · ₹
                                        {(group.totalTaxable / 100).toLocaleString('en-IN', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    </button>
                                  </td>
                                </tr>,
                                ...(shut ? [] : group.rows.map((row, i) => invoiceRow(row, gi + i, body, selectedDoc))),
                              ]
                            })
                          : body.rows.map((row, i) => invoiceRow(row, i, body, selectedDoc)))}
                    </tbody>
                  </table>
                </div>
              )}

              {/*
                * STAGE 4 — the only control on this screen that can create
                * money from nothing, which is why it sits BELOW the table
                * rather than beside a row: it is not an edit to anything, and
                * putting it among the per-row actions would make it look like
                * one. The note is required and the CLIENT READS IT, on its own
                * sheet in the workbook.
                */}
              {/*
                * GATED ON THE APPROVAL TRACK, and on nothing else.
                *
                * Two wrong gates were tried first, and the second was worse.
                * `rows.length > 0` hid the control on a document the parser
                * read NOTHING from — which is the canonical case for it,
                * "page 3 was unreadable". Removing that gate put it on HELD
                * documents, whose rows never reach the return at all: the row
                * saved, the screen showed it as clean, and the client's return
                * silently did not contain it.
                *
                * A held document's way forward is the one already on screen —
                * clear its hold, or send a readable copy. The service refuses this
                * too; a gate that lives only in the interface is a gate
                * somebody routes around.
                */}
              {can(DOCUMENT, 'can_update') && body.can_add_rows && (
                <div className="mt-4">
                  {adding === null ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setAdding({ values: {}, reason: '', note: '' })}
                    >
                      <Plus size={13} /> Add a row the parser missed
                    </Button>
                  ) : (
                    <Card className="p-3">
                      <p className="gst-meta mb-2">
                        A row no document produced. It goes through the same checks as
                        every other row, and <strong>the client&rsquo;s workbook names
                        it</strong> with the reason you give.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {body.addable_fields.map((f) => (
                          <label key={f} className="gst-meta">
                            {f}
                            <input
                              value={adding.values[f] ?? ''}
                              onChange={(e) =>
                                setAdding({
                                  ...adding,
                                  values: { ...adding.values, [f]: e.target.value },
                                })
                              }
                              className="gst-input w-28"
                            />
                          </label>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <select
                          value={adding.reason}
                          onChange={(e) => setAdding({ ...adding, reason: e.target.value })}
                          className="gst-select"
                        >
                          <option value="">Why?</option>
                          {Object.entries(body.money_reasons).map(([code, label]) => (
                            <option key={code} value={code}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <input
                          value={adding.note}
                          onChange={(e) =>
                            setAdding({ ...adding, note: e.target.value.slice(0, 500) })
                          }
                          placeholder="Where did this row come from? The client will see this"
                          className="gst-input min-w-72 flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            !adding.reason ||
                            !adding.note.trim() ||
                            !Object.values(adding.values).some((v) => v.trim())
                          }
                          onClick={() =>
                            void act(async () => {
                              await review.addRow(selectedDoc.id, {
                                line: Object.fromEntries(
                                  Object.entries(adding.values).filter(([, v]) => v.trim()),
                                ),
                                reason_code: adding.reason,
                                note: adding.note,
                              })
                              setAdding(null)
                            })
                          }
                        >
                          Add the row
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setAdding(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {body.orphaned.length > 0 && (
                <Card className="mt-3 p-3 text-xs text-muted-foreground">
                  {body.orphaned.length === 1
                    ? 'One earlier edit no longer matches any row'
                    : `${body.orphaned.length} earlier edits no longer match any row`}{' '}
                  — the re-extraction changed those rows; review their fields:{' '}
                  {[...new Set(body.orphaned.map((o) => body.field_words[o.field] ?? 'a field'))].join(
                    ', ',
                  )}
                  .
                </Card>
              )}
            </>
          )}
        </section>

        {/* -- the document, beside the values ------------------------------ */}
        {sourceOpen && selectedDoc && (
          <aside className="w-[460px] shrink-0">
            <Card className="sticky top-4 flex h-[calc(100vh-8rem)] flex-col p-3">
              <p className="gst-meta mb-2 shrink-0 truncate">
                {selectedDoc.source_filename}
              </p>
              {sourceError ? (
                <div className="gst-empty">
                  {sourceError}{' '}
                  <button type="button" className="gst-link" onClick={() => void openSource()}>
                    Reload
                  </button>
                </div>
              ) : !source ? (
                <div className="gst-empty">Opening…</div>
              ) : source.render === 'pdf' ? (
                <iframe
                  title={selectedDoc.source_filename ?? 'Uploaded document'}
                  src={source.url}
                  referrerPolicy="no-referrer"
                  className="min-h-0 flex-1 rounded border-0"
                />
              ) : source.render === 'image' ? (
                <div className="min-h-0 flex-1 overflow-auto rounded bg-muted/30">
                  <img
                    alt={selectedDoc.source_filename ?? 'Uploaded document'}
                    src={source.url}
                    referrerPolicy="no-referrer"
                    className="mx-auto block"
                  />
                </div>
              ) : (
                /*
                 * `none` — a register. NEVER an iframe: a spreadsheet served
                 * inline makes the browser DOWNLOAD it, which inside the app
                 * looks like a spontaneous unexplained download. The
                 * predecessor shipped exactly that bug. And a register is not
                 * read anyway — it is compared, and the comparison surface is
                 * the table already on the left.
                 */
                <div className="gst-empty flex-1">
                  <p className="mb-2">
                    {selectedDoc.source?.content_type?.includes('spreadsheet') ||
                    selectedDoc.source?.content_type?.includes('ms-excel')
                      ? 'An Excel workbook'
                      : selectedDoc.source?.content_type === 'text/csv'
                        ? 'A CSV register'
                        : 'This file type'}{' '}
                    cannot be shown here — the rows beside it are what to check.
                  </p>
                  <a
                    href={source.url}
                    download={selectedDoc.source_filename ?? undefined}
                    referrerPolicy="no-referrer"
                    className={buttonClass({ variant: 'outline', size: 'sm', className: 'inline-flex' })}
                  >
                    <FileDown size={13} /> Download
                  </a>
                </div>
              )}
            </Card>
          </aside>
        )}
      </div>
    </div>
  )
}
